/**
 * SwitchVlans — Switch > VLAN Library (catalog kind "network"). Was "VLANs" until OP#1858, the
 * same word as the deployable site table (Gateway > Site VLANs); "Library" = definitions only.
 *
 * VLANs are canonical, org-scoped catalog objects (a "Virtual Network"): define once,
 * reference from Port Profiles and SSIDs by id (never inlined). CRUD over the generic
 * /api/v2/catalog/network surface; reuses the Meridian list+dialog pattern.
 */
import { useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { useCatalog, useSaveCatalogObject, useDeleteCatalogObject, type CatalogObject } from "@/lib/api/hooks";
import { PageHeader, ErrorBanner, EmptyState } from "@/components/ui/Page";
import { Button } from "@/components/ui/Button";
import { RoleGate } from "@/components/auth/RoleGate";

// Shared input chrome (matches RadiusServers/SSIDs).
const INPUT = "rounded border border-line bg-surface px-3 py-2 text-ink outline-none focus:border-brand";

export default function SwitchVlans() {
  const { data, isLoading, error } = useCatalog("network");
  const [editing, setEditing] = useState<CatalogObject | null>(null);
  const [creating, setCreating] = useState(false);
  const rows = data?.items ?? [];

  return (
    <div>
      <div className="flex items-start justify-between gap-3">
        <PageHeader title="VLAN Library" subtitle="Virtual networks — define once, reference from port profiles and SSIDs by id" />
        <RoleGate min="org_admin" mode="hide">
          <Button variant="primary" onClick={() => setCreating(true)}>Add VLAN</Button>
        </RoleGate>
      </div>

      {error && <ErrorBanner error={error} />}
      {isLoading && <div className="text-sm text-ink-muted">Loading VLANs…</div>}

      {data && rows.length === 0 && (
        <EmptyState
          title="No VLANs yet"
          hint="Create a VLAN (e.g. Guest = 100, Corp = 10). Port profiles and SSIDs then reference it by name instead of a hard-coded tag."
        />
      )}

      {rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="text-ink-subtle">
              <tr className="[&>th]:py-1.5 [&>th]:pr-4 [&>th]:font-medium">
                <th>Name</th><th>VLAN ID</th><th>Subnet</th><th>Gateway</th><th>Mode</th><th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((n) => (
                <tr key={n.id} className="border-t border-line/60 [&>td]:py-2 [&>td]:pr-4">
                  <td className="font-medium text-ink">{n.name}</td>
                  <td className="font-mono text-ink-muted">{(n.vlan_id as number) ?? "—"}</td>
                  <td className="font-mono text-ink-muted">{(n.subnet as string) || "—"}</td>
                  <td className="font-mono text-ink-muted">{(n.gateway as string) || "—"}</td>
                  <td className="text-ink-muted">{n.dynamic_vlan ? "Dynamic" : "Static"}</td>
                  <td className="text-right">
                    <RoleGate min="org_admin" mode="hide">
                      <Button variant="secondary" onClick={() => setEditing(n)}>Edit</Button>
                    </RoleGate>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {creating && <VlanDialog onClose={() => setCreating(false)} />}
      {editing && <VlanDialog obj={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

function VlanDialog({ obj, onClose }: { obj?: CatalogObject; onClose: () => void }) {
  const save = useSaveCatalogObject("network");
  const del = useDeleteCatalogObject("network");
  const editing = !!obj;

  const [name, setName] = useState(obj?.name ?? "");
  const [vlanId, setVlanId] = useState(obj?.vlan_id != null ? String(obj.vlan_id) : "");
  const [subnet, setSubnet] = useState((obj?.subnet as string) ?? "");
  const [gateway, setGateway] = useState((obj?.gateway as string) ?? "");
  const [dynamic, setDynamic] = useState(!!obj?.dynamic_vlan);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    save.mutate(
      {
        ...(editing ? { id: obj!.id } : {}),
        name: name.trim(),
        vlan_id: vlanId ? Number(vlanId) : null,
        dynamic_vlan: dynamic,
        subnet: subnet.trim() || null,
        gateway: gateway.trim() || null,
      },
      { onSuccess: onClose },
    );
  };

  return (
    <Dialog.Root open onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-sheet bg-ink/40" />
        <Dialog.Content className="md-dialog fixed left-1/2 top-1/2 z-sheet w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg border border-line bg-overlay p-5 shadow-sheet outline-none">
          <Dialog.Title className="text-lg font-semibold text-ink">
            {editing ? `Edit ${obj!.name}` : "Add VLAN"}
          </Dialog.Title>
          <Dialog.Description className="mt-1 text-sm text-ink-muted">
            A virtual network / VLAN. Referenced by name from port profiles and SSIDs.
          </Dialog.Description>
          <form onSubmit={submit} className="mt-4 flex flex-col gap-3">
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-ink-muted">Name</span>
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Guest" className={INPUT} />
            </label>
            <div className="grid grid-cols-2 gap-2">
              <label className="flex flex-col gap-1 text-sm">
                <span className="text-ink-muted">VLAN ID (1–4094, blank = native)</span>
                <input inputMode="numeric" value={vlanId}
                  onChange={(e) => setVlanId(e.target.value.replace(/[^0-9]/g, ""))}
                  placeholder="100" className={`${INPUT} font-mono`} />
              </label>
              <label className="flex flex-col justify-end gap-1 text-sm">
                <label className="flex items-center gap-2 py-2">
                  <input type="checkbox" checked={dynamic} onChange={(e) => setDynamic(e.target.checked)} />
                  <span className="text-ink-muted">Dynamic (RADIUS-assigned)</span>
                </label>
              </label>
            </div>
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-ink-muted">Subnet (optional)</span>
              <input value={subnet} onChange={(e) => setSubnet(e.target.value)} placeholder="192.168.100.0/24" className={`${INPUT} font-mono`} />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-ink-muted">Gateway (optional)</span>
              <input value={gateway} onChange={(e) => setGateway(e.target.value)} placeholder="192.168.100.1" className={`${INPUT} font-mono`} />
            </label>

            {save.isError && <span className="text-xs text-critical">Could not save the VLAN.</span>}
            <div className="md-dialog-footer flex items-center justify-between gap-2">
              {editing ? (
                <RoleGate min="org_admin" mode="hide">
                  <Button variant="danger" type="button" disabled={del.isPending}
                    onClick={() => del.mutate(obj!.id, { onSuccess: onClose })}>
                    {del.isPending ? "…" : "Delete"}
                  </Button>
                </RoleGate>
              ) : <span />}
              <div className="flex items-center gap-2">
                <Button variant="ghost" type="button" onClick={onClose}>Cancel</Button>
                <Button variant="primary" type="submit" disabled={save.isPending}>
                  {save.isPending ? "Saving…" : "Save"}
                </Button>
              </div>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
